import React, { useState } from 'react';
import { FileCode, ArrowRight, Check, Building2, User, MapPin, Loader2, Plus, X, ChevronLeft } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usersApi, teamsApi } from '../services/creatorApi';
import { toast } from 'sonner';
type Step = 'welcome' | 'user' | 'team' | 'regions' | 'review';
interface OnboardingData {
  userName: string;
  userEmail: string;
  teamName: string;
  regions: string[];
}
export function Onboarding() {
  const {
    identifyUser,
    completeOnboarding
  } = useWorkspace();
  const [step, setStep] = useState<Step>('welcome');
  const [data, setData] = useState<OnboardingData>({
    userName: '',
    userEmail: '',
    teamName: '',
    regions: []
  });
  const [newRegion, setNewRegion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleNext = () => {
    if (step === 'welcome') setStep('user');else if (step === 'user') {
      if (data.userName && data.userEmail) setStep('team');
    } else if (step === 'team') {
      if (data.teamName) setStep('regions');
    } else if (step === 'regions') setStep('review');
  };
  const handleBack = () => {
    if (step === 'user') setStep('welcome');else if (step === 'team') setStep('user');else if (step === 'regions') setStep('team');else if (step === 'review') setStep('regions');
  };
  const handleAddRegion = (e: React.FormEvent) => {
    e.preventDefault();
    if (newRegion.trim() && !data.regions.includes(newRegion.trim())) {
      setData({
        ...data,
        regions: [...data.regions, newRegion.trim()]
      });
      setNewRegion('');
    }
  };
  const handleRemoveRegion = (region: string) => {
    setData({
      ...data,
      regions: data.regions.filter(r => r !== region)
    });
  };
  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      // 1. Create Team
      const teamId = teamsApi.generateTeamId(data.teamName);
      const regionRows = data.regions.map(r => ({
        Region_Name: r
      }));
      try {
        await teamsApi.create({
          Team_Name: data.teamName,
          Team_ID: teamId,
          ...(regionRows.length > 0 ? {
            Team_Regions: regionRows
          } : {})
        });
      } catch (teamErr: any) {
        const msg = teamErr?.message || 'Unknown error';
        const debug = teamErr?.debug ? JSON.stringify(teamErr.debug, null, 2) : '';
        console.error('Team creation failed:', teamErr);
        toast.error(`Failed to create team: ${msg}`, {
          description: debug ? `Debug: ${debug.substring(0, 200)}` : undefined,
          duration: 10000
        });
        setIsSubmitting(false);
        return;
      }
      // 2. Create User (Super Admin)
      try {
        await usersApi.create({
          User_Name: data.userName,
          Email: data.userEmail,
          Role: 'Super Admin',
          Status: 'Active',
          Team: data.teamName,
          Invited_By: 'System'
        });
      } catch (userErr: any) {
        const msg = userErr?.message || 'Unknown error';
        const debug = userErr?.debug ? JSON.stringify(userErr.debug, null, 2) : '';
        console.error('User creation failed:', userErr);
        toast.error(`Team created, but user creation failed: ${msg}`, {
          description: debug ? `Debug: ${debug.substring(0, 200)}` : undefined,
          duration: 10000
        });
        setIsSubmitting(false);
        return;
      }
      // 3. Set session and refresh
      localStorage.setItem('configconsole_user_email', data.userEmail);
      await completeOnboarding();
      toast.success('Workspace setup complete!');
    } catch (err: any) {
      console.error('Onboarding failed:', err);
      const msg = err?.message || 'Unknown error';
      toast.error(`Setup failed: ${msg}`, {
        duration: 10000
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  // Progress indicator
  const steps: Step[] = ['welcome', 'user', 'team', 'regions', 'review'];
  const currentStepIndex = steps.indexOf(step);
  const progress = currentStepIndex / (steps.length - 1) * 100;
  return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-id="element-5578" data-mp="i5n3t7nlk391">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-id="element-5579" data-mp="n3f3woyeavob">
        {/* Progress Bar */}
        <div className="h-1 bg-gray-100 w-full" data-id="element-5580" data-mp="8dhfc7wk03yl">
          <div className="h-full bg-blue-600 transition-all duration-500 ease-out" style={{
          width: `${progress}%`
        }} data-id="element-5581" data-mp="d5h9tktuu4ns" />
        </div>

        <div className="p-8" data-id="element-5582" data-mp="3fo7laflr1w7">
          {/* Header */}
          <div className="flex items-center justify-between mb-8" data-id="element-5583" data-mp="3g7elw3ps5vg">
            <div className="flex items-center gap-2" data-id="element-5584" data-mp="3llaqbzo6rg4u">
              <div className="h-8 w-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-5585" data-mp="3lnwvjsnltjjw">
                <FileCode className="h-5 w-5 text-blue-600" data-id="element-5586" data-mp="3qmhwjuaok79e" />
              </div>
              <span className="font-semibold text-gray-900" data-id="element-5587" data-mp="36z3tpdb3qffc">ConfigConsole</span>
            </div>
            {step !== 'welcome' && <span className="text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-5588" data-mp="3gwrr6b03e117">
                Step {currentStepIndex} of 4
              </span>}
          </div>

          {/* Step Content */}
          <div className="min-h-[300px] flex flex-col" data-id="element-5589" data-mp="3pwy0dkorceuk">
            {step === 'welcome' && <div className="flex-1 flex flex-col items-center text-center justify-center animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5590" data-mp="3kz6asxbl54tj">
                <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-6" data-id="element-5591" data-mp="3hd8lcvao137">
                  <Building2 className="h-8 w-8 text-blue-600" data-id="element-5592" data-mp="3uqizjg237evc" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-5593" data-mp="cj32cpcnfc45">
                  Welcome to ConfigConsole
                </h1>
                <p className="text-gray-500 mb-8 max-w-sm" data-id="element-5594" data-mp="fkfve1z2carn">
                  Let's get your workspace set up. We'll create your admin
                  account and configure your first team.
                </p>
                <button onClick={handleNext} className="w-full bg-gray-900 text-white rounded-lg px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2" data-id="element-5595" data-mp="pi2gj66ygyk2">
                  Get Started
                  <ArrowRight className="h-4 w-4" data-id="element-5596" data-mp="139qr351k3eyx" />
                </button>
              </div>}

            {step === 'user' && <div className="flex-1 animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-5597" data-mp="18aer2g7rj65a">
                <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5598" data-mp="1d6vgddqp7qjy">
                  Create your account
                </h2>
                <p className="text-sm text-gray-500 mb-6" data-id="element-5599" data-mp="3a2ji7phftzey">
                  You'll be the Super Admin for this workspace.
                </p>
                <div className="space-y-4" data-id="element-5600" data-mp="1w4ovdqsu55ia">
                  <div data-id="element-5601" data-mp="1w5q7zzpi2024">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5602" data-mp="1rdqccjggptbc">
                      Full Name
                    </label>
                    <div className="relative" data-id="element-5603" data-mp="1nd6dnbg8qhn3">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5604" data-mp="1n9qxe5atz2k3" />
                      <input type="text" className="block w-full pl-10 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2.5" placeholder="e.g. John Doe" value={data.userName} onChange={e => setData({
                    ...data,
                    userName: e.target.value
                  })} autoFocus data-id="element-5605" data-mp="2f3aw34kfwulo" />
                    </div>
                  </div>
                  <div data-id="element-5606" data-mp="2pqcma46rg96t">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5607" data-mp="2pp1b3jazjz5o">
                      Email Address
                    </label>
                    <div className="relative" data-id="element-5608" data-mp="2fsoiypv39td1">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5609" data-mp="2ftzkej12ap0z">
                        @
                      </div>
                      <input type="email" className="block w-full pl-10 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2.5" placeholder="john@company.com" value={data.userEmail} onChange={e => setData({
                    ...data,
                    userEmail: e.target.value
                  })} data-id="element-5610" data-mp="38gkbrjhjf14j" />
                    </div>
                  </div>
                </div>
              </div>}

            {step === 'team' && <div className="flex-1 animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-5611" data-mp="1fsw8d8lvrp8t">
                <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5612" data-mp="1uh9547sslc4d">
                  Create your first team
                </h2>
                <p className="text-sm text-gray-500 mb-6" data-id="element-5613" data-mp="1pjqsa6bnmcr3">
                  Teams help organize your workspace. You can add more later.
                </p>
                <div data-id="element-5614" data-mp="wtqne7h5kzo8">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5615" data-mp="wujvpcvrfx8h">
                    Team Name
                  </label>
                  <div className="relative" data-id="element-5616" data-mp="1auum6et5r9t4">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5617" data-mp="16s6faco08cys" />
                    <input type="text" className="block w-full pl-10 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2.5" placeholder="e.g. Marketing, Global, North America" value={data.teamName} onChange={e => setData({
                  ...data,
                  teamName: e.target.value
                })} autoFocus data-id="element-5618" data-mp="2m85e2xbqewmd" />
                  </div>
                </div>
              </div>}

            {step === 'regions' && <div className="flex-1 animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-5619" data-mp="3l3i3wf73w39r">
                <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5620" data-mp="3h1wnijvd56ut">
                  Add regions (Optional)
                </h2>
                <p className="text-sm text-gray-500 mb-6" data-id="element-5621" data-mp="3c4wfw4iv7w1l">
                  Define geographic or logical regions for this team.
                </p>

                <form onSubmit={handleAddRegion} className="flex gap-2 mb-4" data-id="element-5622" data-mp="1ac89jmaxcshl">
                  <div className="relative flex-1" data-id="element-5623" data-mp="1aa47zb0i05kh">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5624" data-mp="1hug91ee5iaqi" />
                    <input type="text" className="block w-full pl-10 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2.5" placeholder="e.g. New York, London" value={newRegion} onChange={e => setNewRegion(e.target.value)} data-id="element-5625" data-mp="1mt9kz2dshtok" />
                  </div>
                  <button type="submit" disabled={!newRegion.trim()} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 font-medium text-sm" data-id="element-5626" data-mp="13w8ch7l11bfv">
                    Add
                  </button>
                </form>

                <div className="space-y-2 max-h-48 overflow-y-auto" data-id="element-5627" data-mp="2objrhu54e3x2">
                  {data.regions.length === 0 ? <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg" data-id="element-5628" data-mp="p6a7kscaekw2">
                      <p className="text-sm text-gray-400" data-id="element-5629" data-mp="k8i739xktqg9">
                        No regions added yet
                      </p>
                    </div> : data.regions.map(region => <div key={region} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-5630" data-mp="t96mo92mbyv9">
                        <span className="text-sm font-medium text-gray-700" data-id="element-5631" data-mp="1j22obirtduk">
                          {region}
                        </span>
                        <button onClick={() => handleRemoveRegion(region)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-5632" data-mp="ahduggvdihol">
                          <X className="h-4 w-4" data-id="element-5633" data-mp="3dxkxcl5ysawb" />
                        </button>
                      </div>)}
                </div>
              </div>}

            {step === 'review' && <div className="flex-1 animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-5634" data-mp="1y9eo9kp178zx">
                <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5635" data-mp="1khq896bnw0do">
                  Review & Complete
                </h2>
                <p className="text-sm text-gray-500 mb-6" data-id="element-5636" data-mp="2d4u8kxzt7qj4">
                  Double check your details before we set everything up.
                </p>

                <div className="space-y-4 bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-5637" data-mp="2h5mk5xw5m7ik">
                  <div className="flex items-start gap-3" data-id="element-5638" data-mp="235va288rdb9u">
                    <div className="h-8 w-8 bg-white rounded-full flex items-center justify-center border border-gray-200 flex-shrink-0" data-id="element-5639" data-mp="284yg9w0h5nje">
                      <User className="h-4 w-4 text-gray-500" data-id="element-5640" data-mp="rpn3zwu4bhtf" />
                    </div>
                    <div data-id="element-5641" data-mp="wr43pp0v8y8f">
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide" data-id="element-5642" data-mp="wrm5t2a7m6o9">
                        Admin Account
                      </p>
                      <p className="text-sm font-medium text-gray-900" data-id="element-5643" data-mp="1786kzgcnd8ay">
                        {data.userName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-5644" data-mp="137w77him5dlj">{data.userEmail}</p>
                    </div>
                  </div>

                  <div className="h-px bg-gray-200" data-id="element-5645" data-mp="1uytxyg1f6o4d" />

                  <div className="flex items-start gap-3" data-id="element-5646" data-mp="1uyadarkdxlmn">
                    <div className="h-8 w-8 bg-white rounded-full flex items-center justify-center border border-gray-200 flex-shrink-0" data-id="element-5647" data-mp="1qwp5hkmpjxjb">
                      <Building2 className="h-4 w-4 text-gray-500" data-id="element-5648" data-mp="1lwkm9muf0ope" />
                    </div>
                    <div data-id="element-5649" data-mp="1gyj2t0t2unde">
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide" data-id="element-5650" data-mp="1h15cp73fxeys">
                        Team
                      </p>
                      <p className="text-sm font-medium text-gray-900" data-id="element-5651" data-mp="5iufg8bwvmc2">
                        {data.teamName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-5652" data-mp="3rn61tm1hsp6k">
                        {data.regions.length === 0 ? 'No regions defined' : `${data.regions.length} region${data.regions.length === 1 ? '' : 's'}: ${data.regions.join(', ')}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
          </div>

          {/* Footer Navigation */}
          {step !== 'welcome' && <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100" data-id="element-5653" data-mp="j7mef3ni60zf">
              <button onClick={handleBack} className="text-sm font-medium text-gray-500 hover:text-gray-900 flex items-center gap-1" data-id="element-5654" data-mp="o2jl43xb7y95">
                <ChevronLeft className="h-4 w-4" data-id="element-5655" data-mp="2pxkbd4ukjm8u" />
                Back
              </button>

              {step === 'review' ? <button onClick={handleComplete} disabled={isSubmitting} className="bg-blue-600 text-white rounded-lg px-6 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-70" data-id="element-5656" data-mp="2muefko0nwnim">
                  {isSubmitting ? <>
                      <Loader2 className="h-4 w-4 animate-spin" data-id="element-5657" data-mp="2rs65jchqxzk6" />
                      Setting up...
                    </> : <>
                      Complete Setup
                      <Check className="h-4 w-4" data-id="element-5658" data-mp="30s317y2jzyoq" />
                    </>}
                </button> : <button onClick={handleNext} disabled={step === 'user' && (!data.userName || !data.userEmail) || step === 'team' && !data.teamName} className="bg-gray-900 text-white rounded-lg px-6 py-2.5 text-sm font-medium hover:bg-gray-800 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5659" data-mp="3ak8ncr64jlw0">
                  Next Step
                  <ArrowRight className="h-4 w-4" data-id="element-5660" data-mp="3u60riccg239u" />
                </button>}
            </div>}
        </div>
      </div>
    </div>;
}